/**
 * WHAT THE PAPER SHELL IS ALLOWED TO ASK OF THE HOST PAGE. `game-ui.ts` draws and focuses;
 * every effect on the voyage comes back through these seams, built by `world-entry.ts` once
 * it has a session and, on a startup failure, by `reportFatal` with no world at all - which is
 * why they are a function over seams rather than closures inside `main`.
 *
 * It lives in its own file so a test can drive the REAL adapter over a session in memory on an
 * injected clock (host-commands.test.ts): the two proofs Task 5 owes - a first reveal inside
 * fifteen seconds, and Reel leaving every byte of the save alone - are proved through the same
 * object the shell calls, not through a copy of it. `world-entry.ts` cannot be imported by a
 * test (it pulls in PixiJS and the baked art), so nothing in here reads `window` except the
 * `reload` seam the host supplies.
 */
import type { ImportResult, SaveStorage } from '../sim/index';
import type { GlyphFamily } from './actions';
import type { GameCommands } from './game-ui';
import type { PlayerSession, StartResult } from './player-session';
import type { AppPresentation } from './presentation';
import { hasValidSave } from './voyage-entry';
import type { OfflineReturnView } from './offline-return';
import { advanceTutorial, skipTutorial } from './tutorial';
import { finishAtlas, continueFishing } from './season';
import type { TransactionResult } from './outfitter';

export interface HostSeams {
  readonly dev: boolean;
  /** The save keys Continue Voyage looks under: the dev key under `?dev`, the real chain otherwise. */
  readonly saveKeys: readonly string[];
  now(): number;
  storage(): SaveStorage;
  player(): PlayerSession;
  voyageRunning(): boolean;
  /** Begin ticking the player's session in place of the preview. */
  startReal(): void;
  /** Stop and save the player's session; the preview returns under the title. */
  stopReal(): void;
  /** The voyage under `running` was replaced: rebase everything that cached it. */
  freshRun(): void;
  /** Push the profile's settings into the audio engine and the page. */
  applySettings(): void;
  presentation(): AppPresentation | null;
  ledgerOpen(): boolean;
  /** The glyph family of the last input used, for the `auto` preference. */
  lastInput(): GlyphFamily;
  /** Reel, now: the ceremony decides whether it is inside its window. Presentation only. */
  reel(): boolean;
  offlineReturn?(): OfflineReturnView | null;
  dismissOfflineReturn?(): void;
  reload(): void;
}

export function buildCommands(h: HostSeams): GameCommands {
  const saved = (): boolean => hasValidSave(h.storage(), h.now(), h.saveKeys);
  // A preference changes the save only if there is one: the title preview must not create a
  // save by having its text size changed. Before the first Set Sail the change lives in the
  // dormant session and rides into the first autosave.
  const persist = (): void => { if (h.voyageRunning() || saved()) h.player().save(); };
  const swapped = (r: { readonly ok: boolean }): void => { if (r.ok) { h.freshRun(); h.applySettings(); } };
  const appearance = (r: TransactionResult): TransactionResult => { if (r.ok) h.applySettings(); return r; };
  return {
    hasValidSave: saved,
    saveProblem: () => h.player().saveProblem(),
    saveFailure: () => h.player().saveFailure(),
    recovery: () => h.player().recovery().inspect(),
    exportSave: () => h.player().exportSave(),
    exportBackup: () => h.player().recovery().exportRaw(),
    importSave: (text): ImportResult => { const r = h.player().importSave(text); swapped(r); return r; },
    restoreBackup: (): ImportResult => { const r = h.player().recovery().restore(h.now()); swapped(r); return r; },
    startNew: (): StartResult => {
      const r = h.player().startNew();
      swapped(r);
      return r;
    },
    continueVoyage: (): StartResult => {
      if (h.player().saveProblem() !== null) return { ok: false, reason: 'save-needs-recovery' };
      if (!saved() && !h.voyageRunning()) return { ok: false, reason: 'no-valid-save' };
      const written = h.player().save(); if (!written.written) return { ok: false, reason: written.note };
      if (!h.voyageRunning()) h.startReal();
      return h.player().saveFailure() === null ? { ok: true } : { ok: false, reason: h.player().saveFailure()! };
    },
    setSail: (confirmedReplacement = false, identityDraft): StartResult => {
      const p = h.player();
      const problem = p.saveProblem();
      if (problem?.kind === 'unreadable') return { ok: false, reason: problem.reason };
      if (saved() || problem !== null) {
        if (!confirmedReplacement) return { ok: false, reason: 'confirm-new-voyage' };
        // The candidate includes identity and retained preferences before its single write.
        const r = p.startNew(identityDraft);
        if (!r.ok) return r;
        h.freshRun();
      } else if (identityDraft !== undefined) {
        const r = p.startNew(identityDraft);
        if (!r.ok) return r;
        h.freshRun();
      } else {
        const written = p.save(); if (!written.written) return { ok: false, reason: written.note };
      }
      h.applySettings();
      if (!h.voyageRunning()) h.startReal();
      return p.saveFailure() === null ? { ok: true } : { ok: false, reason: p.saveFailure()! };
    },
    returnToTitle: () => h.stopReal(),
    identity: () => h.player().profile().identity,
    updateIdentity: (identity) => appearance(h.player().updateIdentity(identity, h.now())),
    harbour: () => h.player().harbour(h.now()),
    cancelCommission: () => h.player().cancelCommission(h.now()),
    acceptCommission: id => h.player().acceptCommission(id, h.now()),
    claimCommission: id => h.player().claimCommission(id, h.now()),
    buyHarbourUpgrade: id => h.player().buyHarbourUpgrade(id, h.now()),
    fundHarbourProject: id => h.player().fundHarbourProject(id, h.now()),
    completeHarbourChallenge: proof => h.player().completeHarbourChallenge(proof, h.now()),
    outfitter: () => h.player().outfitter(),
    buyCosmetic: (id) => appearance(h.player().buyCosmetic(id, h.now())),
    equipCosmetic: (slot, id) => appearance(h.player().equipCosmetic(slot, id, h.now())),
    settings: () => h.player().profile().settings,
    updateSettings: (patch) => { h.player().updateSettings(patch); h.applySettings(); persist(); },
    tutorial: () => h.player().profile().tutorial,
    setTutorial: (mode) => { h.player().setTutorial(mode); if (mode.kind === 'unseen') h.player().updateProfile(profile => ({ ...profile, skippedTutorialSteps: [] })); persist(); },
    skippedTips: () => h.player().profile().skippedTutorialSteps,
    skipTip: () => {
      const mode = h.player().profile().tutorial;
      if (mode.kind !== 'active' && mode.kind !== 'replay') return;
      h.player().updateProfile(profile => ({ ...profile, skippedTutorialSteps: profile.skippedTutorialSteps.includes(mode.stepId) ? profile.skippedTutorialSteps : [...profile.skippedTutorialSteps, mode.stepId], tutorial: skipTutorial(mode, false) }));
      persist();
    },
    presentation: () => h.presentation(),
    glyphs: () => { const g = h.player().profile().settings.glyphs; return g === 'auto' ? h.lastInput() : g; },
    ledgerOpen: () => h.ledgerOpen(),
    // Presentation only: the ceremony records the instant and Task 3's model does the rest.
    // No tick, no save, no RNG is reachable from here (host-commands.test.ts proves the bytes).
    reel: () => (h.voyageRunning() ? h.reel() : false),
    setTarget: (id) => { if (!h.voyageRunning()) return; h.player().setTarget(id, h.now()); h.player().setTutorial(advanceTutorial(h.player().profile().tutorial, { kind: 'target' })); persist(); },
    sailTo: (id) => { const result = h.player().sailTo(id, h.now()); if (result.ok) { h.player().setTutorial(advanceTutorial(h.player().profile().tutorial, { kind: 'destination' })); persist(); } return result; },
    returnToNaturalCourse: () => { if (!h.voyageRunning()) return; h.player().returnToNaturalCourse(h.now()); h.player().setTutorial(advanceTutorial(h.player().profile().tutorial, { kind: 'return' })); persist(); },
    offlineReturn: () => h.offlineReturn?.() ?? null,
    dismissOfflineReturn: () => h.dismissOfflineReturn?.(),
    finishAtlas: () => { h.player().updateProfile(finishAtlas); persist(); },
    continueFishing: () => { h.player().updateProfile(continueFishing); persist(); },
    savedAt: () => saved() ? h.player().voyage().lastSavedMs() >= 0 ? h.player().voyage().lastSavedMs() : h.player().profile().lastSavedAtMs : null,
    reload: () => h.reload(),
  };
}
